import { useModifyAudioDevices } from '@hakimo-ui/hakimo/data-access';
import { AudioDevice } from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Alert, Button, InputField, Modal } from '@hakimo-ui/shared/ui-base';
import { PencilIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { MAX_GAP_SECONDS, MIN_GAP_SECONDS } from '../util';

interface Props {
  audioDevice: AudioDevice;
}

const AutomatedTalkdownCooldown: React.FC<Props> = ({ audioDevice }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [gapValue, setGapValue] = useState(
    audioDevice.automatedTalkdownGapSec || 0
  );

  const { mutate: updateAudioDevice, isLoading } = useModifyAudioDevices(() => {
    toast('Automated talkdown cooldown updated', { type: 'success' });
    setIsOpen(false);
  });

  const handleSubmit = () => {
    updateAudioDevice({
      id: audioDevice.id,
      automatedTalkdownGapSec: gapValue,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value >= MIN_GAP_SECONDS && value <= MAX_GAP_SECONDS) {
      setGapValue(value);
    }
  };

  const modalFooter = (
    <>
      <Button
        variant="primary"
        onClick={handleSubmit}
        loading={isLoading}
        disabled={isLoading}
      >
        Update
      </Button>
      <Button
        variant="outline"
        onClick={() => setIsOpen(false)}
        disabled={isLoading}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <>
      <Button variant="icon" onClick={() => setIsOpen(true)}>
        <PencilIcon className="h-4 w-4" />
      </Button>

      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Update Auto Talkdown Cooldown"
        footer={modalFooter}
      >
        <div className="p-6 px-10">
          <div className="space-y-6">
            <Alert type="info">
              Maximum cooldown duration is {MAX_GAP_SECONDS} seconds
            </Alert>
            <div>
              <InputField
                type="number"
                label="Gap Duration (seconds)"
                value={gapValue?.toString()}
                onChange={handleInputChange}
                min={MIN_GAP_SECONDS?.toString()}
                max={MAX_GAP_SECONDS?.toString()}
              />
              <div className="mt-2 flex items-center space-x-4 pt-2">
                <input
                  type="range"
                  min={MIN_GAP_SECONDS}
                  max={MAX_GAP_SECONDS}
                  value={gapValue}
                  onChange={handleInputChange}
                  className="onlight:bg-onlight-bg-2 ondark:bg-ondark-bg-2 accent-primary h-2 flex-1 cursor-pointer rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AutomatedTalkdownCooldown;
